import React, { useRef, useLayoutEffect, useState, useEffect } from "react";
import { Button, Col, Row } from "react-bootstrap";
import TextInput from "../../../../common/TextInput";
import {
  formatAppointmentDateTime,
  formattedDate,
} from "../../../../utils/dateUtils";
import { formatDurationHHMMSS } from "../../../../utils/formatDurationHHMMSS";
import DictationModelDataTable from "./DictationModelDataTable";
import {
  downloadSingleFileForDictationsPopup,
  downloadZipFileForDictations,
} from "../../../../services/EditorWorkFlowAPI";
import UploadStatusFloatingIcon from "../../../../common/UploadStatusFloatingIcon";

const getAdjustedPosition = (
  position: { top: number; left?: number; right?: number } | null,
  elementHeight: number
) => {
  if (!position) {
    return { top: "20%", left: "50%", transform: "translateX(-50%)" };
  }

  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;
  const modalWidth = viewportWidth * 0.5;
  const top = position.top;
  let left =
    position.left ?? viewportWidth - (position.right ?? 0) - modalWidth;

  if (left + modalWidth > viewportWidth) {
    left = viewportWidth - modalWidth - 10;
  }
  if (left < 10) {
    left = 10;
  }

  const spaceBelow = viewportHeight - top;
  const adjustedTop =
    spaceBelow < elementHeight ? top - elementHeight - 10 : top;

  return {
    top: adjustedTop,
    left,
    transform: "none",
  };
};

interface DictationModalModalProps {
  show: boolean;
  onClose: () => void;
  data: any;
  position?: { top: number; left?: number; right?: number } | null;
  selectedRowData: any;
}

function convertToDictationGroups(apiData: any) {
  let dictationFiles = [];

  if (Array.isArray(apiData.dictationFiles)) {
    dictationFiles = apiData.dictationFiles;
  } else if (typeof apiData.dictationFiles === "string") {
    try {
      dictationFiles = JSON.parse(apiData.dictationFiles);
    } catch {
      dictationFiles = [];
    }
  }

  let additionalFiles = [];

  if (Array.isArray(apiData.additionalFiles)) {
    additionalFiles = apiData.additionalFiles;
  } else if (typeof apiData.additionalFiles === "string") {
    try {
      additionalFiles = JSON.parse(apiData.additionalFiles);
    } catch {
      additionalFiles = [];
    }
  }

  const formatItems = (files: any[], baseUrl: string, isAdditional = false) =>
    files?.map((file: any, index: number) => ({
      srNo: String(index + 1).padStart(2, "0"),
      requestDictationAttachmentID: file.RequestDictationAttachmentID,
      dictationID: file.RequestID,
      fileName: file.FileName || "-",
      recordingDuration: file?.RecordingDuration
        ? formatDurationHHMMSS(file?.RecordingDuration)
        : "-",
      additionalFileInfo: file.AdditionalFileInfo ?? "-",
      uploadDateTime: formatAppointmentDateTime(file.UploadDateTime) ?? "-",
    }));

  return [
    {
      id: "dictationFiles",
      title: "Dictation Files",
      items: formatItems(dictationFiles, "/dictation", false),
    },
    {
      id: "additionalFiles",
      title: "Additional Files",
      items: formatItems(additionalFiles, "/additional", true),
    },
  ];
}

const DictationModal: React.FC<DictationModalModalProps> = ({
  show,
  onClose,
  data,
  position,
  selectedRowData,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [adjustedPos, setAdjustedPos] = useState<any>(null);
  const [downloadStatuses, setDownloadStatuses] = useState<any>({});

  useLayoutEffect(() => {
    if (show && position && modalRef.current) {
      const elementHeight = modalRef.current.offsetHeight || 300;
      setAdjustedPos(getAdjustedPosition(position, elementHeight));
    }
  }, [show, position]);

  useEffect(() => {
    if (!show) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [show, onClose]);

  if (!show) return null;

  const dictationGroups = convertToDictationGroups(data);

  const handleDownloadProgress = async (
    id: string,
    downloadFn: () => Promise<any>,
    fileName: string = "File.zip"
  ) => {
    setDownloadStatuses((prev: any) => ({
      ...prev,
      [id]: { fileName, progress: 0, isUploading: true },
    }));

    let progress = 0;
    const interval = setInterval(() => {
      progress += 10;
      if (progress >= 90) return;
      setDownloadStatuses((prev: any) => ({
        ...prev,
        [id]: { ...prev[id], progress },
      }));
    }, 300);

    try {
      await downloadFn();
      clearInterval(interval);
      setDownloadStatuses((prev: any) => ({
        ...prev,
        [id]: { ...prev[id], progress: 100, isUploading: false },
      }));
      setTimeout(() => {
        setDownloadStatuses((prev: any) => {
          const updated = { ...prev };
          delete updated[id];
          return updated;
        });
      }, 2000);
    } catch (error) {
      console.error("Download error:", error);
      clearInterval(interval);
      setDownloadStatuses((prev: any) => ({
        ...prev,
        [id]: { ...prev[id], isUploading: false },
      }));
    }
  };
  
  const handleDownloadAll = () => {
    const subjectQmeScheduleIDs = selectedRowData?.subjectQmeScheduleID;
    handleDownloadProgress(`doc-${Date.now()}`, () =>
      downloadZipFileForDictations(subjectQmeScheduleIDs)
    );
  };

  const handleDownloadSingleFile = (id: number, fileName: string) => {
    handleDownloadProgress(
      `doc-${Date.now()}`,
      () => downloadSingleFileForDictationsPopup(id),
      fileName
    );
  };

  return (
    <>
      <UploadStatusFloatingIcon
        uploadStatuses={downloadStatuses}
        isUpload={false}
        onCancelUpload={(id: any) => {
          setDownloadStatuses((prev: any) => {
            const updated = { ...prev };
            delete updated[id];
            return updated;
          });
        }}
      />
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          // background: 'rgba(0,0,0,0.4)',
          zIndex: 99,
        }}
      />

      <div
        className="aaa"
        ref={modalRef}
        style={{
          position: "fixed",
          width: "50%",
          height: "300px",
          background: "#fff",
          border: "1px solid #ccc",
          borderRadius: "8px",
          boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
          overflow: "hidden",
          zIndex: 100,
          ...(adjustedPos ?? {
            top: "20%",
            left: "50%",
            transform: "translateX(-50%)",
          }),
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "2px 12px",
            fontSize: "15px",
          }}
        >
          <span className="btn-text fw-medium">Details</span>
          <Button
            variant="light"
            size="sm"
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              fontSize: "16px",
            }}
            className="btn-text fw-bold px-0"
          >
            <span className="material-symbols-outlined">close</span>
          </Button>
        </div>

        <div className="mx-3">
          <div className="gx-3 gy-2 mb-2 d-flex justify-content-between align-items-center">
            <TextInput
              label="Total Duration"
              name="totalDurations"
              value={formatDurationHHMMSS(data?.totalRecordingDuration)}
              className="dictation-readable-textbox"
              disabled
            />

            <Button
              variant="outline"
              className="btn d-flex align-items-center btn-text-border"
              size="sm"
              onClick={handleDownloadAll}
            >
              <span
                className="material-icons me-1 "
                style={{ fontSize: "18px" }}
              >
                download
              </span>
              Download All
            </Button>
          </div>

          <div
            style={{
              // maxHeight: '135px',
              overflowY: "auto",
              marginTop: "10px",
            }}
          >
            <DictationModelDataTable
              dictationGroups={dictationGroups}
              onDownloadAction={handleDownloadSingleFile}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default DictationModal;
